import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { fetchBotsData } from '../services/dashboardService';
import '../styles/DashboardLayout.css';

const DashboardLayout = ({ children, activeTab, setActiveTab, title, selectedBot, setSelectedBot }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Bots dropdown state
  const [bots, setBots] = useState([]);
  const [showBotsDropdown, setShowBotsDropdown] = useState(false);
  const [botsLoading, setBotsLoading] = useState(false);
  
  // Market Intelligence dropdown state
  const [showIntelligenceDropdown, setShowIntelligenceDropdown] = useState(false);

  // Fetch bots data
  const loadBots = async () => {
    if (bots.length > 0) {
      // Already loaded, just toggle dropdown
      setShowBotsDropdown(!showBotsDropdown);
      return;
    }
    
    try {
      setBotsLoading(true);
      const botsData = await fetchBotsData();
      if (botsData.success) {
        setBots(botsData.data.bots || []);
        setShowBotsDropdown(true);
      }
    } catch (err) {
      console.error('Error loading bots:', err);
    } finally {
      setBotsLoading(false);
    }
  };

  const handleBotClick = (bot) => {
    if (setSelectedBot) {
      setSelectedBot(bot);
    }
    if (setActiveTab) {
      setActiveTab('bots');
    }
  };

  const toggleTheme = () => {
    const currentTheme = document.documentElement.getAttribute('data-theme');
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);
  };

  const navItems = [
    { id: 'overview', label: 'Overview', icon: 'fas fa-home', path: '/dashboard' },
    { id: 'bots', label: 'My Bots', icon: 'fas fa-robot', path: '/dashboard' },
    { 
      id: 'intelligence', 
      label: 'Market Intelligence', 
      icon: 'fas fa-chart-line', 
      path: '/dashboard',
      hasDropdown: true,
      subItems: [
        { id: 'omni-search', label: 'Omni Search', icon: 'fas fa-magnifying-glass-chart' },
        { id: 'whiteboard', label: 'Whiteboard', icon: 'fas fa-chalkboard' },
        { id: 'intelligence', label: 'Properties', icon: 'fas fa-building' },
        { id: 'knowledge-graph', label: 'Knowledge Graph', icon: 'fas fa-diagram-project' },
        { id: 'intelligence-analytics', label: 'Analytics Dashboard', icon: 'fas fa-chart-bar' }
      ]
    },
    { id: 'news', label: 'News Feed', icon: 'fas fa-newspaper', path: '/dashboard' },
    { id: 'pdf', label: 'Generated Reports', icon: 'fas fa-file-pdf', path: '/dashboard' },
    { id: 'requests', label: 'New Request', icon: 'fas fa-plus-circle', path: '/dashboard' }
  ];

  const getPageTitle = () => {
    let activeItem = navItems.find(item => item.id === activeTab);
    
    if (!activeItem) {
      for (const item of navItems) {
        if (item.subItems) {
          const subItem = item.subItems.find(si => si.id === activeTab);
          if (subItem) {
            activeItem = subItem;
            break;
          }
        }
      }
    }
    
    return activeItem ? activeItem.label : 'Dashboard';
  };

  return (
    <div className={`app-container ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
            <div className="sidebar-header">
                <div className="logo">
                    <div className="logo-square"></div>
                    {!isCollapsed && <span>Realista</span>}
                </div>
                <button 
                    className="sidebar-toggle" 
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                >
                    <i className={`fas fa-${isCollapsed ? 'indent' : 'outdent'}`}></i>
                </button>
            </div>
            
            <ul className="nav-menu">
                {navItems.map(item => (
                    <li className="nav-item" key={item.id}>
                        <div 
                            className={`nav-link ${activeTab === item.id || (item.subItems && item.subItems.some(si => si.id === activeTab)) ? 'active' : ''}`}
                            onClick={() => {
                                if (item.id === 'bots') {
                                    loadBots();
                                } else if (item.hasDropdown) {
                                    if (item.id === 'intelligence') {
                                        setShowIntelligenceDropdown(!showIntelligenceDropdown);
                                    }
                                } else if (item.path && item.path !== '/dashboard') {
                                    navigate(item.path);
                                } else {
                                    if (location.pathname === '/dashboard') {
                                        if (setActiveTab) setActiveTab(item.id);
                                    } else {
                                        navigate('/dashboard');
                                    }
                                }
                            }}
                            role="button"
                            tabIndex={0}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    if (item.id === 'bots') {
                                        loadBots();
                                    } else if (item.hasDropdown) {
                                        if (item.id === 'intelligence') {
                                            setShowIntelligenceDropdown(!showIntelligenceDropdown);
                                        }
                                    } else if (item.path && item.path !== '/dashboard') {
                                        navigate(item.path);
                                    } else {
                                        if (location.pathname === '/dashboard') {
                                            if (setActiveTab) setActiveTab(item.id);
                                        } else {
                                            navigate('/dashboard');
                                        }
                                    }
                                }
                            }}
                        >
                            <i className={item.icon}></i> 
                            {!isCollapsed && <span>{item.label}</span>}
                            {(item.id === 'bots' || item.hasDropdown) && !isCollapsed && (
                              <i className={`fas fa-chevron-${(item.id === 'bots' ? showBotsDropdown : showIntelligenceDropdown) ? 'down' : 'right'} dropdown-icon`}></i>
                            )}
                        </div>
                        
                        {item.hasDropdown && item.id === 'intelligence' && showIntelligenceDropdown && !isCollapsed && (
                            <ul className="bots-dropdown">
                                {item.subItems.map((subItem) => (
                                    <li 
                                        key={subItem.id}
                                        className={`bots-dropdown-item ${activeTab === subItem.id ? 'selected' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (location.pathname === '/dashboard') {
                                                if (setActiveTab) setActiveTab(subItem.id);
                                            } else {
                                                navigate('/dashboard');
                                            }
                                        }}
                                    >
                                        <i className={subItem.icon}></i>
                                        <span>{subItem.label}</span>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {item.id === 'bots' && showBotsDropdown && !isCollapsed && (
                            <ul className="bots-dropdown">
                                {botsLoading && (
                                    <li className="bots-dropdown-loading">Loading bots...</li>
                                )}
                                {!botsLoading && bots.length === 0 && (
                                    <li className="bots-dropdown-empty">No bots found</li>
                                )}
                                {!botsLoading && bots.map((bot, index) => (
                                    <li 
                                        key={index}
                                        className={`bots-dropdown-item ${selectedBot?.name === bot.name ? 'selected' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleBotClick(bot);
                                        }}
                                    >
                                        <i className="fas fa-robot"></i>
                                        <span>{bot.name}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </li>
                ))}
            </ul>

            <div className="user-profile">
                <div className="avatar"></div>
                {!isCollapsed && <span>{user?.firstName || 'User'} {user?.lastName || ''}</span>}
            </div>
        </aside>

        <main className="main-content">
            <header>
                <div className="header-title">
                    <h2 id="page-title">{getPageTitle()}</h2>
                </div>
                <div className="header-actions">
                    <button className="theme-toggle" onClick={toggleTheme} title="Toggle Dark/Light Mode">
                        <i className="fas fa-moon" id="theme-icon"></i>
                    </button>
                    <button className="btn-signout" onClick={logout}>Sign Out</button>
                </div>
            </header>

            <div className="scroll-area">
                {children}
            </div>
        </main>
    </div>
  );
};

export default DashboardLayout;
